import { LabelledField } from "@mixitone/components";
import { ApplicationView, useController } from "@mixitone/mvc";
import { twMerge } from "tailwind-merge";
import Form from "../Form";
import FormButtons from "../FormButtons";
import { FormController } from "../FormController";
import type { FormFieldConfig } from "../FormFieldsDefinition";
import { ValueComponent, isRequired } from "../FormValueComponents";

interface ModalFormRendererProps {
  name: string;
  fields: FormFieldConfig[];
  showDelete?: boolean;
}

export const ModalFormField = ApplicationView(
  ({ field, record, autoFocus }: { field: FormFieldConfig; record: any; autoFocus?: boolean }) => {
    const variant = field.type === "checkbox" ? "toggle" : undefined;
    if (field.condition && !field.condition(record)) return null;

    if (field.hideLabel) {
      const extraWrapper = field.type === "custom" ? field.wrapperClassName : undefined;
      return (
        <div className={twMerge("mb-4", extraWrapper)}>
          <ValueComponent field={field} record={record} autoFocus={autoFocus} variant={variant} />
        </div>
      );
    }

    return (
      <LabelledField
        label={field.label}
        helpText={field.helpText || field.description}
        inline={field.type === "checkbox"}
        autoFocus={autoFocus}
        required={isRequired(field, record)}
        htmlFor={field.name}
        aria-autocomplete="none"
        data-1p-ignore
      >
        <ValueComponent field={field} record={record} autoFocus={autoFocus} variant={variant} />
      </LabelledField>
    );
  },
);

export const ModalFormFieldset = ApplicationView(
  ({ fields, className }: { fields: FormFieldConfig[]; className?: string }) => {
    const controller = useController(FormController);
    const { record, loading, saving } = controller.state;

    if (loading) return null;

    return (
      <fieldset disabled={saving} className={className}>
        {fields.map((field, i) => (
          <ModalFormField key={field.name} field={field} record={record} autoFocus={i === 0} />
        ))}
      </fieldset>
    );
  },
);

const ModalFormRenderer: React.FC<ModalFormRendererProps> = ({ name, fields, showDelete = true }) => {
  return (
    <Form name={name}>
      <ModalFormFieldset fields={fields} />
      <FormButtons showDelete={showDelete} />
    </Form>
  );
};

export default ApplicationView(ModalFormRenderer);
